/**
 * How a fee is described, shared by the Plans catalog, the enrol wizard and the
 * Enrolments table so they phrase it identically.
 *
 * There is one shape now: an amount. This file used to switch across five
 * frequencies, instalment splits and recurring-vs-not, all of which went on
 * 2026-08-25 when a fee became a single payment. See the header of
 * lambda/routes/stripe-collections.ts for why.
 *
 * Rows written before that still carry a `frequency`, so it is rendered where
 * a human would notice its absence, and ignored everywhere else.
 */
import { Membership, Subscription } from '../api/client';

function pounds(pence: number): string {
  return `£${((Number(pence) || 0) / 100).toFixed(2)}`;
}

export interface ChargeDescription {
  /** Short label for a badge. */
  kind: string;
  /** One line a club admin can read at a glance. */
  summary: string;
}

/** A plan: what it costs. */
export function describePlan(m: Membership): ChargeDescription {
  return { kind: 'Fee', summary: pounds(m.totalPrice) };
}

/** An enrolment: what this player's place costs. */
export function describeSubscriptionCharge(s: Subscription): ChargeDescription {
  // Legacy rows carry a cadence. Say so rather than silently describing a
  // £15/month enrolment as if it were a single £15.
  const legacy = String((s as any).frequency || '');
  if (legacy === 'monthly' || legacy === 'quarterly' || legacy === 'annual') {
    return { kind: 'Legacy', summary: `${pounds(s.amount)} (was ${legacy})` };
  }
  return { kind: 'Fee', summary: pounds(s.amount) };
}
